import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import { message, Button, Space, Checkbox } from "antd";
import { useTranslation } from "react-i18next";
import { formatAssignee } from "@tailormed/common-client/util/formatters";
import {
  executeBulkActionAPI,
  getRecordIdsOnlyAPI,
  getPatientsByIdsAPI,
  getEnrollmentsByIdsAPI,
  getClaimsByIdsAPI
} from "../../api/api";
import { statuses as claimStatuses } from "../../constant/claim";
import { ALL_LANGUAGES } from "../../constant/languages";
import ACTIONS from "../../store/action";
import { PRIMARY_600 } from "../../constant/colors";
import { ReactComponent as SuccessIcon } from "../../assets/svg/success-msg-icon.svg";
import { ReactComponent as WarningIcon } from "../../assets/svg/alert-circle-yellow.svg";
import { ReactComponent as ErrorIcon } from "../../assets/svg/error-msg-icon.svg";
import { ReactComponent as ToastCloseIcon } from "../../assets/svg/toast-close.svg";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import {
  selectAssigneesAndUserGroups,
  selectFilters,
  selectProvidersFacility,
  selectProvidersDoctors,
  selectIsSidebarCollapsed
} from "../../store/selector";
import { selectedViews } from "../../constant/table";
import { UNASSIGNED_VALUE } from "../../constant/filters";
import { useGetSetting } from "../../hooks/getSetting";
import { useStatuses } from "../Patient/PatientApplications/hooks";
import useBulkSelection from "./useBulkSelection";
import BulkActionsBar from "./BulkActionsBar";
import BulkActionDropdown from "./BulkActionDropdown";
import {
  getAvailableActions,
  getActionKey,
  getActionCategory,
  downloadCSVReport,
  formatValueForDisplay,
  BULK_ACTION_VIEWS
} from "./bulkActionUtils";
import { useBulkActionTracking, BulkActionStatus } from "./useBulkActionTracking";

// Wrapper to provide positioning context for the floating action bar
const BulkActionsWrapper = styled.div`
  position: relative;
`;

// Must stay aligned with the single-patient preferred-language field: both read from
// ALL_LANGUAGES so bulk and single-record updates store identical values.
const PREFERRED_LANGUAGE_OPTIONS = ALL_LANGUAGES.map((language) => ({
  value: language.value,
  label: language.label
}));

/**
 * Container component that provides bulk action functionality
 * Uses render props pattern to provide rowSelection to child table
 * Uses inline dropdowns and toast notifications per Figma design
 *
 * @example
 * <BulkActionsContainer viewKey="all-patients" entityType="patient" ...>
 *   {({ rowSelection }) => <Table rowSelection={rowSelection} ... />}
 * </BulkActionsContainer>
 */
const BulkActionsContainer = ({
  viewKey,
  entityType,
  visibleRecordIds,
  totalRecords,
  rowKey = "id",
  additionalFilters,
  isExporting,
  children
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Pendo tracking hook
  const {
    trackBulkActionViewedOnce,
    trackBulkActionTriggered,
    trackBulkActionCompleted,
    trackBulkActionReportDownloaded
  } = useBulkActionTracking();

  // Bulk selection hook
  const {
    selectedRecordIds,
    selectionCount,
    hasSelection,
    isLimitReached,
    toggleRecord,
    clearSelection,
    selectAll,
    isLoading,
    setLoading,
    setResult,
    isRecordSelected,
    setNavigatedAway,
    wouldExceedLimitOnPageSelect,
    isAllPageSelected,
    isIndeterminate,
    togglePageSelection
  } = useBulkSelection(viewKey, visibleRecordIds, totalRecords);

  // Keep a ref of isLoading for use in cleanup effect (to avoid stale closure)
  const isLoadingRef = useRef(isLoading);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Local state — stores the active action object or null
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Redux selectors
  const assignees = useSelector(selectAssigneesAndUserGroups);
  const facilities = useSelector(selectProvidersFacility);
  const physicians = useSelector(selectProvidersDoctors);
  const filters = useSelector(selectFilters);
  const isSidebarCollapsed = useSelector(selectIsSidebarCollapsed);
  const currentViewData = useSelector((state) => state.tablesData?.[viewKey]);

  const [patientStatuses] = useGetSetting(["statuses"]);
  const [bulkUpdatePreferredLanguageEnabled] = useGetSetting(["bulk-actions.update-preferred-language"]);
  const enrollmentStatuses = useStatuses();

  // Track previous filters to detect changes
  const prevFiltersRef = useRef(filters);

  // Clear selection when filters change (to avoid having invisible records selected).
  // If a bulk action is in progress, flag navigatedAway so the global toast appears.
  useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      if (isLoadingRef.current) {
        setNavigatedAway(true);
      }
      clearSelection();
      prevFiltersRef.current = filters;
    }
  }, [filters, clearSelection, setNavigatedAway]);

  // Reset active dropdown when selection is cleared (action bar hidden)
  useEffect(() => {
    if (!hasSelection) {
      setActiveDropdown(null);
    }
  }, [hasSelection]);

  // Track bulk action viewed when selection first occurs
  useEffect(() => {
    if (hasSelection && selectionCount > 0) {
      // Use trackOnce with viewKey to prevent duplicate events within the same selection session
      trackBulkActionViewedOnce(`bulk-action-viewed-${viewKey}`, {
        totalCountSelected: selectionCount
      });
    }
  }, [hasSelection, selectionCount, trackBulkActionViewedOnce, viewKey]);

  // Clear selection when component unmounts (user navigates away from view)
  // If a bulk action is in progress, set navigatedAway flag to show global toast
  useEffect(() => {
    return () => {
      if (isLoadingRef.current) {
        setNavigatedAway(true);
      }
      clearSelection();
    };
  }, [clearSelection, setNavigatedAway]);

  const availableActions = useMemo(() => {
    const actions = getAvailableActions(entityType);
    // "Update Preferred Language" is gated behind a per-site setting (off by default).
    if (!bulkUpdatePreferredLanguageEnabled) {
      return actions.filter((action) => action.actionType !== "preferred_language");
    }
    return actions;
  }, [entityType, bulkUpdatePreferredLanguageEnabled]);

  // Prepare status options for the entity's own status action
  const enrollmentStatusOptions = useMemo(() => {
    if (enrollmentStatuses) {
      return Object.values(enrollmentStatuses)
        .filter((status) => status.isSelectable === true)
        .map((status) => ({
          value: status.value,
          label: status.name
        }));
    }
    return [];
  }, [enrollmentStatuses]);

  const patientStatusOptions = useMemo(() => {
    if (patientStatuses?.length) {
      return patientStatuses.map((status) => ({
        value: status.value,
        label: status.text
      }));
    }
    return [];
  }, [patientStatuses]);

  const claimStatusOptions = useMemo(() => {
    return claimStatuses.map((status) => ({
      value: status.value,
      label: status.label
    }));
  }, []);

  const assigneeOptions = useMemo(() => {
    const userOptions = (assignees || []).map((assignee) => ({
      value: assignee.id,
      label: formatAssignee(assignee),
      item: assignee
    }));
    return [{ value: UNASSIGNED_VALUE, label: t("unassigned"), item: { type: "unassigned" } }, ...userOptions];
  }, [assignees, t]);

  const facilityOptions = useMemo(() => {
    return (facilities || []).map((facility) => ({
      value: facility.id,
      label: facility.name,
      item: facility
    }));
  }, [facilities]);

  const physicianOptions = useMemo(() => {
    return (physicians || []).map((physician) => ({
      value: physician.id,
      label: physician.name,
      item: physician
    }));
  }, [physicians]);

  /**
   * Resolve the correct options list for a given action.
   * Uses the structured { actionType, targetEntity } properties
   * to determine which options apply.
   */
  const getOptionsForAction = useCallback(
    (action) => {
      const { actionType, targetEntity } = action;

      const category = getActionCategory(actionType);

      if (category === "status") {
        if (targetEntity === "patient") return patientStatusOptions;
        if (entityType === "patient") return patientStatusOptions;
        if (entityType === "enrollment") return enrollmentStatusOptions;
        if (entityType === "claim") return claimStatusOptions;
        return [];
      }
      if (category === "assignee" || category === "multi_assignee") return assigneeOptions;
      if (category === "facility" || category === "multi_facility") return facilityOptions;
      if (category === "physician") return physicianOptions;
      if (category === "preferred_language") return PREFERRED_LANGUAGE_OPTIONS;
      return [];
    },
    [
      entityType,
      patientStatusOptions,
      enrollmentStatusOptions,
      claimStatusOptions,
      assigneeOptions,
      facilityOptions,
      physicianOptions
    ]
  );

  // Handlers
  const handleActionClick = useCallback((action) => {
    setActiveDropdown(action);
  }, []);

  const handleDropdownCancel = useCallback(() => {
    setActiveDropdown(null);
  }, []);

  const handleClearSelection = useCallback(() => {
    // Close dropdown first to prevent jitter when action bar hides
    setActiveDropdown(null);
    // Then clear selection (this will trigger action bar to hide)
    clearSelection();
  }, [clearSelection]);

  const handleSelectAll = useCallback(async () => {
    try {
      const mergedFilters = additionalFilters
        ? {
            ...filters,
            ...additionalFilters,
            multiples: { ...filters?.multiples, ...additionalFilters?.multiples }
          }
        : filters;
      const response = await getRecordIdsOnlyAPI(viewKey, mergedFilters);
      const allIds = response.ids || [];
      selectAll(allIds);

      // If any visible records aren't in the selection, the page is stale.
      // Re-fetch page 1 so the table shows filter-matching (selected) records.
      const allIdsSet = new Set(allIds);
      const hasStaleVisibleRecords = visibleRecordIds.some((id) => !allIdsSet.has(id));
      if (hasStaleVisibleRecords) {
        dispatch(ACTIONS.paginateSortChange(1, viewKey, {}));
      }
    } catch (error) {
      ErrorMessage(t("bulk_actions.errors.generic"));
    }
  }, [viewKey, filters, additionalFilters, visibleRecordIds, selectAll, dispatch, t]);

  const showResultToast = useCallback(
    (response, actionType, targetEntity, value, options, trackingContext) => {
      const { recordCount: total, succeededCount, failedCount, results } = response;

      // Format value for display
      const formattedValue = formatValueForDisplay(value, actionType, options, t);

      // Create metadata for CSV report
      const metadata = {
        actionType,
        targetEntity,
        entityType,
        value: formattedValue,
        timestamp: new Date(),
        summary: {
          total,
          succeeded: succeededCount,
          failed: failedCount
        }
      };

      // Handler for download button with Pendo tracking
      const handleDownloadReport = () => {
        // Track report download event
        if (trackingContext) {
          trackBulkActionReportDownloaded(trackingContext);
        }
        downloadCSVReport(results, metadata, t);
      };

      const downloadLinkTextOnly = (
        <Button
          type="link"
          size="small"
          onClick={handleDownloadReport}
          data-pendo="bulk_action.report_download"
          style={{ padding: "0 8px", height: "auto", color: PRIMARY_600, fontWeight: 600 }}
        >
          {t("bulk_actions.results.download_report")}
        </Button>
      );

      if (failedCount === 0) {
        // Full success - 10 seconds with close button
        const recordLabel = t("bulk_actions.record", { count: succeededCount });
        const successKey = `success-${Date.now()}`;
        message.open({
          key: successKey,
          type: "success",
          content: (
            <Space style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <SuccessIcon />
              <span style={{ flex: 1 }}>
                {t("bulk_actions.results.success_message", { count: succeededCount, entity: recordLabel })}{" "}
                {downloadLinkTextOnly}
              </span>
              <ToastCloseIcon onClick={() => message.destroy(successKey)} style={{ cursor: "pointer" }} />
            </Space>
          ),
          duration: 10,
          icon: null,
          className: "success-message"
        });
      } else if (succeededCount === 0) {
        // Full failure - persistent until closed
        const errorKey = `error-${Date.now()}`;
        message.open({
          key: errorKey,
          type: "error",
          content: (
            <Space style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <ErrorIcon />
              <span style={{ flex: 1 }}>
                {t("bulk_actions.results.failure_message")} {downloadLinkTextOnly}
              </span>
              <ToastCloseIcon onClick={() => message.destroy(errorKey)} style={{ cursor: "pointer" }} />
            </Space>
          ),
          duration: 0,
          icon: null,
          className: "error-message"
        });
      } else {
        // Partial success - persistent until closed
        const warningKey = `warning-${Date.now()}`;
        message.open({
          key: warningKey,
          type: "warning",
          content: (
            <Space style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <WarningIcon />
              <span style={{ flex: 1 }}>
                {t("bulk_actions.results.partial_message", {
                  succeeded: succeededCount,
                  total
                })}{" "}
                {downloadLinkTextOnly}
              </span>
              <ToastCloseIcon onClick={() => message.destroy(warningKey)} style={{ cursor: "pointer" }} />
            </Space>
          ),
          duration: 0,
          icon: null,
          className: "warning-message"
        });
      }
    },
    [entityType, t, trackBulkActionReportDownloaded]
  );

  /**
   * Execute a bulk action. Receives structured data directly from the dropdown
   * (actionType, targetEntity, value) — no string parsing needed.
   */
  const handleExecute = useCallback(
    async ({ actionType, targetEntity, value }) => {
      setActiveDropdown(null);
      setLoading(true);

      const UPDATE_MODE_BY_ACTION = {
        assignees_set: "Override",
        assignees_add: "Add",
        assignees_remove: "Remove",
        assignees_clear: "Override",
        facilities_set: "Override",
        facilities_add: "Add",
        facilities_remove: "Remove"
      };

      const getAttribute = (action) => {
        const actionCategory = getActionCategory(action);
        if (actionCategory === "assignee" || actionCategory === "multi_assignee") return "assignee";
        if (actionCategory === "facility" || actionCategory === "multi_facility") return "facility";
        return actionType;
      };

      const attribute = getAttribute(actionType);
      const updateMode = UPDATE_MODE_BY_ACTION[actionType] || "Override";
      const totalCountSelected = selectionCount;

      // Track bulk action triggered event
      trackBulkActionTriggered({
        attribute,
        updateMode,
        totalCountSelected
      });

      try {
        const apiPayload = {
          recordType: entityType,
          actionType,
          recordIds: selectedRecordIds,
          value
        };
        if (targetEntity) {
          apiPayload.targetEntity = targetEntity;
        }
        const response = await executeBulkActionAPI(apiPayload);

        setResult(response);

        // Determine status based on results
        const { succeededCount, failedCount } = response;
        let status;
        if (failedCount === 0) {
          status = BulkActionStatus.SUCCESS;
        } else if (succeededCount === 0) {
          status = BulkActionStatus.FAILURE;
        } else {
          status = BulkActionStatus.PARTIAL;
        }

        // Track bulk action completed event
        trackBulkActionCompleted({
          attribute,
          updateMode,
          totalCountSelected,
          recordsSucceeded: succeededCount,
          recordsFailed: failedCount,
          status
        });

        // Get options for the action to format value for display
        const options = getOptionsForAction({ actionType, targetEntity });

        // Pass tracking context for report download tracking
        const trackingContext = {
          attribute,
          updateMode,
          totalCountSelected,
          recordsSucceeded: succeededCount,
          recordsFailed: failedCount,
          status
        };

        // Clear loading state before showing toast so the action bar updates first
        setLoading(false);
        setNavigatedAway(false);

        showResultToast(response, actionType, targetEntity, value, options, trackingContext);

        // Refresh affected records in place
        // 1. Extract success IDs
        const successIds = response.results.filter((r) => r.success).map((r) => r.recordId);

        // 2. Intersect with currently visible record IDs
        const visibleSuccessIds = successIds.filter((id) => visibleRecordIds.includes(id));

        // 3. If there are visible records to refresh, fetch and update in place
        if (visibleSuccessIds.length > 0) {
          try {
            if (entityType === "patient") {
              const viewKeyToApiSelectedView = {
                "all-patients": selectedViews.allPatients,
                "visit-schedule": selectedViews.visitSchedule,
                "high-priority": selectedViews.topOpportunity,
                "top-opportunity": selectedViews.topOpportunity
              };
              const viewKeyToStoreKey = {
                "all-patients": selectedViews.allPatients,
                "visit-schedule": selectedViews.visitSchedule,
                "high-priority": selectedViews.highPriority,
                "top-opportunity": selectedViews.topOpportunity
              };
              const apiSelectedView = viewKeyToApiSelectedView[viewKey] || viewKey;
              const storeKey = viewKeyToStoreKey[viewKey] || viewKey;
              const updatedData = await getPatientsByIdsAPI(apiSelectedView, visibleSuccessIds, filters);

              // If some patients came back empty (lost access), remove them from the table
              if (updatedData.patients) {
                const returnedPatientIds = updatedData.patients.map((p) => p.patientId);
                const missingPatientIds = visibleSuccessIds.filter((id) => !returnedPatientIds.includes(id));

                if (updatedData.patients.length > 0) {
                  // Update patients that still have access
                  dispatch(ACTIONS.updatePatientsInPlace(storeKey, updatedData.patients));
                }

                if (missingPatientIds.length > 0) {
                  // Remove patients that no longer match the filter or lost access
                  // (not returned by API), then clear selection so the bulk action
                  // bar closes instead of showing a now-stale selected count.
                  dispatch(ACTIONS.removePatientsFromTable(storeKey, missingPatientIds));
                  clearSelection();
                }
              }
            } else if (entityType === "enrollment") {
              const apiSelectedView = selectedViews.allApplications;
              const storeKey = viewKey || selectedViews.allApplications;

              let enrollmentIdsToRefetch = visibleSuccessIds;

              // For cross-entity updates (e.g., patient status/assignee changed from
              // the enrollment view), also refresh sibling enrollments belonging to
              // the same patients so they reflect the updated patient-level data.
              if (targetEntity && currentViewData?.patients) {
                const affectedPatientIds = new Set(
                  response.results
                    .filter((result) => result.success && result.patientId != null)
                    .map((result) => result.patientId)
                );

                if (affectedPatientIds.size > 0) {
                  // currentViewData.patients holds the visible enrollment rows
                  // The API uses "patients" as a generic key for all view types
                  enrollmentIdsToRefetch = currentViewData.patients
                    .filter((enrollment) => affectedPatientIds.has(enrollment.patientId))
                    .map((enrollment) => enrollment.applicationId);
                }
              }

              const updatedData = await getEnrollmentsByIdsAPI(apiSelectedView, enrollmentIdsToRefetch, filters);

              // If some enrollments came back empty (lost access), remove them from the table
              if (updatedData.patients) {
                const returnedEnrollmentIds = updatedData.patients.map((e) => e.applicationId);
                const missingEnrollmentIds = enrollmentIdsToRefetch.filter((id) => !returnedEnrollmentIds.includes(id));

                if (updatedData.patients.length > 0) {
                  // Update enrollments that still have access
                  dispatch(ACTIONS.updateEnrollmentsInPlace(storeKey, updatedData.patients));
                }

                if (missingEnrollmentIds.length > 0) {
                  // Remove enrollments that no longer match the filter or lost access
                  // (not returned by API), then clear selection so the bulk action
                  // bar closes instead of showing a now-stale selected count.
                  dispatch(ACTIONS.removeEnrollmentsFromTable(storeKey, missingEnrollmentIds));
                  clearSelection();
                }
              }
            } else if (entityType === "claim") {
              const apiSelectedView = selectedViews.allClaims;
              const storeKey = viewKey || selectedViews.allClaims;

              let claimIdsToRefetch = visibleSuccessIds;

              if (targetEntity && currentViewData?.patients) {
                const affectedPatientIds = new Set(
                  response.results
                    .filter((result) => result.success && result.patientId != null)
                    .map((result) => result.patientId)
                );

                if (affectedPatientIds.size > 0) {
                  claimIdsToRefetch = currentViewData.patients
                    .filter((claim) => affectedPatientIds.has(claim.patientId))
                    .map((claim) => claim.claimId);
                }
              }

              const updatedData = await getClaimsByIdsAPI(apiSelectedView, claimIdsToRefetch, filters);

              // If some claims came back empty (lost access), remove them from the table
              if (updatedData.patients) {
                const returnedClaimIds = updatedData.patients.map((c) => c.claimId);
                const missingClaimIds = claimIdsToRefetch.filter((id) => !returnedClaimIds.includes(id));

                if (updatedData.patients.length > 0) {
                  // Update claims that still have access
                  dispatch(ACTIONS.updateClaimsInPlace(storeKey, updatedData.patients));
                }

                if (missingClaimIds.length > 0) {
                  // Remove claims that no longer match the filter or lost access
                  // (not returned by API), then clear selection so the bulk action
                  // bar closes instead of showing a now-stale selected count.
                  dispatch(ACTIONS.removeClaimsFromTable(storeKey, missingClaimIds));
                  clearSelection();
                }
              }
            }
          } catch (refreshError) {
            console.error("Failed to refresh updated records:", refreshError);
          }
        }

        // Re-fetch other views so cross-entity denormalized data stays fresh
        BULK_ACTION_VIEWS.filter((view) => view !== viewKey).forEach((view) =>
          dispatch(ACTIONS.setTableDataAction(null, view))
        );
      } catch (error) {
        // Clear loading state before showing error toast
        setLoading(false);
        setNavigatedAway(false);
        ErrorMessage(t("bulk_actions.errors.generic"));
      }
    },
    [
      entityType,
      filters,
      selectedRecordIds,
      selectionCount,
      setLoading,
      setResult,
      showResultToast,
      clearSelection,
      viewKey,
      visibleRecordIds,
      currentViewData,
      dispatch,
      t,
      getOptionsForAction,
      setNavigatedAway,
      trackBulkActionTriggered,
      trackBulkActionCompleted
    ]
  );

  // Render inline dropdown for an action (wraps the button with a Popover)
  // barContainerRef is passed from BulkActionsBar to attach popover to the fixed bar
  const renderActionDropdown = useCallback(
    (action, buttonElement, barContainerRef) => {
      const options = getOptionsForAction(action);
      const isActive = activeDropdown && getActionKey(activeDropdown) === getActionKey(action);

      return (
        <BulkActionDropdown
          visible={isActive}
          actionType={action.actionType}
          targetEntity={action.targetEntity}
          entityType={entityType}
          options={options}
          onConfirm={handleExecute}
          onCancel={handleDropdownCancel}
          targetRef={buttonElement}
          popupContainerRef={barContainerRef}
        />
      );
    },
    [activeDropdown, getOptionsForAction, handleExecute, handleDropdownCancel, entityType]
  );

  // Handle row selection change from Ant Design Table
  const handleRowSelectionChange = useCallback(
    (newSelectedRowKeys) => {
      const currentIds = new Set(selectedRecordIds);
      const newIds = new Set(newSelectedRowKeys);

      // Find records to toggle (changed state)
      visibleRecordIds.forEach((id) => {
        const wasSelected = currentIds.has(id);
        const isNowSelected = newIds.has(id);

        if (wasSelected !== isNowSelected) {
          toggleRecord(id);
        }
      });
    },
    [selectedRecordIds, visibleRecordIds, toggleRecord]
  );

  // Get checkbox props for individual rows
  const getCheckboxProps = useCallback(
    (record) => {
      const recordId = record[rowKey];
      return {
        disabled: isLimitReached && !isRecordSelected(recordId),
        "data-testid": `bulk-select-row-${recordId}`
      };
    },
    [rowKey, isLimitReached, isRecordSelected]
  );

  // Custom header checkbox that can be disabled when selecting page would exceed limit
  const headerCheckbox = useMemo(
    () => (
      <Checkbox
        checked={isAllPageSelected}
        indeterminate={isIndeterminate}
        disabled={wouldExceedLimitOnPageSelect}
        onChange={togglePageSelection}
        data-pendo="bulk_action.select_page"
        data-testid="bulk-select-page-checkbox"
      />
    ),
    [isAllPageSelected, isIndeterminate, wouldExceedLimitOnPageSelect, togglePageSelection]
  );

  // Ant Design Table rowSelection configuration
  const rowSelection = useMemo(
    () => ({
      type: "checkbox",
      selectedRowKeys: selectedRecordIds,
      onChange: handleRowSelectionChange,
      getCheckboxProps,
      preserveSelectedRowKeys: true,
      columnWidth: 32,
      columnTitle: headerCheckbox
    }),
    [selectedRecordIds, handleRowSelectionChange, getCheckboxProps, headerCheckbox]
  );

  // Support both render props and regular children
  const renderChildren = () => {
    if (typeof children === "function") {
      return children({ rowSelection });
    }
    return children;
  };

  return (
    <BulkActionsWrapper>
      {renderChildren()}

      <BulkActionsBar
        visible={hasSelection}
        selectionCount={selectionCount}
        totalRecords={totalRecords}
        availableActions={availableActions}
        entityType={entityType}
        onClearSelection={handleClearSelection}
        onSelectAll={handleSelectAll}
        onActionClick={handleActionClick}
        isExecuting={isLoading}
        isExporting={isExporting}
        renderActionDropdown={renderActionDropdown}
        activeAction={activeDropdown}
        isSidebarCollapsed={isSidebarCollapsed}
      />
    </BulkActionsWrapper>
  );
};

BulkActionsContainer.propTypes = {
  viewKey: PropTypes.string.isRequired,
  entityType: PropTypes.oneOf(["patient", "enrollment", "claim"]).isRequired,
  visibleRecordIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  totalRecords: PropTypes.number.isRequired,
  rowKey: PropTypes.string,
  isExporting: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired
};

BulkActionsContainer.defaultProps = {
  rowKey: "id",
  isExporting: false
};

export default BulkActionsContainer;
